/* eslint-disable no-void */
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  createFilterOptions,
} from '@mui/material'

import { isArray, isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { autoComplete } from '../api'

const AutoComplete = ({
  metodo,
  codigo,
  label,
  value,
  textFieldProps,
  onChange = (e) => void e,
  getOptionLabel,
  searchParams,
  filterFunction,
  carregarAoAbrir = true,
  ...props
}) => {
  const [lista, setLista] = useState([])
  const [inputValue, setInputValue] = useState('')
  const searchParamsAnterior = useRef(searchParams)

  const searchParamsRef = useRef(searchParams)

  useEffect(() => {
    searchParamsRef.current = searchParams
  }, [searchParams])

  const buscar = useCallback(
    async (valor = '') => {
      try {
        const res = await autoComplete(
          metodo,
          {
            value: valor,
            ...searchParamsRef.current,
          },
          codigo,
        )

        setLista(res.result || [])
      } catch (error) {
        console.error(error)
        setLista([])
      }
    },
    [codigo, metodo],
  )

  /**
   * Carrega itens iniciais ao abrir componente
   */
  useEffect(() => {
    if (carregarAoAbrir) {
      buscar('')
    }
  }, [buscar, carregarAoAbrir])

  /**
   * Atualiza texto quando value mudar externamente
   */
  useEffect(() => {
    if (value) {
      const texto = getOptionLabel?.(value) || value?.at?.(1) || ''

      setInputValue(typeof texto === 'string' ? texto : '')
    } else {
      setInputValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => JSON.stringify(option),
    ignoreAccents: true,
    ignoreCase: true,
  })

  useEffect(() => {
    if (!isEqual(searchParamsAnterior.current, searchParams)) {
      searchParamsAnterior.current = searchParams
      buscar('')
    }
  }, [searchParams, buscar])

  return (
    <MuiAutocomplete
      options={
        isArray(value)
          ? lista.filter((v) =>
              filterFunction
                ? filterFunction(v, value)
                : !isEqual(v?.[0], value?.[0]),
            )
          : lista
      }
      isOptionEqualToValue={(option, val) => option?.[0] === val?.[0]}
      getOptionLabel={getOptionLabel || ((option) => option?.at?.(1) || '')}
      noOptionsText="Nenhum registro encontrado"
      clearText="Limpar"
      openText="Abrir"
      filterSelectedOptions
      autoComplete
      autoHighlight
      filterOptions={filterOptions}
      value={value || null}
      inputValue={inputValue}
      onChange={(event, newValue) => {
        onChange(event, newValue)
      }}
      onInputChange={(event, newInputValue, reason) => {
        const valor = newInputValue || ''

        setInputValue(valor)

        /**
         * Faz busca apenas digitando
         */
        if (reason === 'input') {
          buscar(valor)
        }

        /**
         * Limpou campo
         */
        if (reason === 'clear') {
          buscar('')
        }
      }}
      {...props}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          margin="dense"
          variant="outlined"
          size="small"
          label={label}
          InputLabelProps={{
            ...params.InputLabelProps,
            ...(textFieldProps?.InputLabelProps || {}),
            shrink: true,
          }}
        />
      )}
    />
  )
}

AutoComplete.propTypes = {
  metodo: PropTypes.string.isRequired,
  codigo: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  searchParams: PropTypes.object,
  onChange: PropTypes.func,
  getOptionLabel: PropTypes.func,
  textFieldProps: PropTypes.object,
  filterFunction: PropTypes.func,
  carregarAoAbrir: PropTypes.bool,
}

export default memo(AutoComplete, isEqual)
